import React, { useEffect } from 'react';
import { getIn, useFormikContext } from 'formik';

import AlertBanner from '~/components/core/AlertBanner';
import Chip from '~/components/core/Atomic/Chip/Chip';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import { CopyToClipboardIcon } from '~/components/icons';
import CheckIcon from '~/components/icons/CheckIcon';
import CloseIcon from '~/components/icons/CloseIcon';
import LoadingIndicator from '~/components/LoadingIndicator';
import TextFieldFormik from '~/components/TextFieldFormik';
import cn from '~/Utils/cn';

import type { MetabaseQuestion } from './hooks/useMetabaseQuestions';
import { getAllVariablesMatchedText, getUnmatchedVariablesWarning, REPORT_TEMPLATES_TEXTS } from './texts';
import type { SheetValidationState } from './types';

interface SheetConfigurationCardProps {
  sheetName: string;
  index: number;
  questions: MetabaseQuestion[];
  isLoadingQuestions: boolean;
  isSubmitting: boolean;
  validationState?: SheetValidationState;
  onQuestionValidation: (sheetName: string, questionId: number) => void;
}

const SheetConfigurationCard: React.FC<SheetConfigurationCardProps> = ({
  sheetName,
  index,
  questions,
  isLoadingQuestions,
  isSubmitting,
  validationState,
  onQuestionValidation,
}) => {
  const { values } = useFormikContext();
  const currentQuestionId = getIn(values, `sheets.${index}.metabase_question_id`);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    if (currentQuestionId) {
      onQuestionValidation(sheetName, currentQuestionId);
    }
  }, [currentQuestionId, sheetName, onQuestionValidation]);

  const hasUnmatchedVariables = (validationState?.unmatchedVariables.length ?? 0) > 0;
  const hasMatchedVariables = (validationState?.matchedVariables.length ?? 0) > 0;
  const hasVariables = (validationState?.extracted_variables.length ?? 0) > 0;
  const isValidating = validationState?.isValidating ?? false;
  const availableColumns = validationState?.availableColumns ?? [];

  const handleCopyToken = async (column: string) => {
    try {
      await navigator.clipboard.writeText(`{{${column}}}`);
      showSnackbar(REPORT_TEMPLATES_TEXTS.COPY_TOKEN_SUCCESS, 'success');
    } catch {
      showSnackbar(REPORT_TEMPLATES_TEXTS.COPY_TOKEN_ERROR, 'error');
    }
  };

  return (
    <div className="mb-16 rounded-lg border border-slate-200 p-16">
      <div className="mb-12 flex items-center justify-between">
        <Heading variant={Heading.TYPES.H4}>
          {REPORT_TEMPLATES_TEXTS.SHEET_PREFIX} {sheetName}
        </Heading>
      </div>

      <div className="grid grid-cols-2 gap-12">
        <div>
          <TextFieldFormik
            id={`sheets.${index}.display_name`}
            label={REPORT_TEMPLATES_TEXTS.SHEET_TEMPLATE_NAME_LABEL}
            fullWidth
            disabled={isSubmitting}
          />
        </div>

        <div>
          {/* @ts-expect-error TextFieldFormik types don't support MenuItem children when used as select - fix in TextFieldFormik.d.ts */}
          <TextFieldFormik
            id={`sheets.${index}.metabase_question_id`}
            label={REPORT_TEMPLATES_TEXTS.SELF_SERVICE_TABLE_LABEL}
            select
            fullWidth
            disabled={isSubmitting || isLoadingQuestions}
          >
            <MenuItem value="">{REPORT_TEMPLATES_TEXTS.SELECT_TABLE_PLACEHOLDER}</MenuItem>
            {questions.map((q) => (
              <MenuItem key={q.id} value={q.id}>
                {q.name}
              </MenuItem>
            ))}
          </TextFieldFormik>
        </div>

        {hasVariables && (
          <div className="col-span-2">
            <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.MEDIUM} className="mb-8">
              {REPORT_TEMPLATES_TEXTS.EXTRACTED_VARIABLES} ({validationState?.extracted_variables.length})
            </Text>
            <div className="flex flex-wrap gap-4">
              {validationState?.extracted_variables.map((variable) => {
                const isMatched = validationState.matchedVariables.includes(variable);
                const isUnmatched = validationState.unmatchedVariables.includes(variable);

                return (
                  <Chip
                    key={variable}
                    label={
                      <span className="flex items-center gap-4">
                        {variable}
                        {isMatched && <CheckIcon className="h-12 w-12 text-teal-600" />}
                        {isUnmatched && <CloseIcon className="text-red-600 h-12 w-12" />}
                      </span>
                    }
                    size="small"
                    className={cn(
                      isMatched && 'bg-teal-100 text-teal-800',
                      isUnmatched && 'text-red-800 bg-red-100',
                      !isMatched && !isUnmatched && 'bg-slate-100'
                    )}
                  />
                );
              })}
            </div>
          </div>
        )}

        {availableColumns.length > 0 && !isValidating && (
          <div className="col-span-2">
            <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.MEDIUM} className="mb-4">
              {REPORT_TEMPLATES_TEXTS.AVAILABLE_COLUMNS} ({availableColumns.length})
            </Text>
            <Text variant={Text.VARIANTS.XS} className="mb-8 text-slate-500">
              {REPORT_TEMPLATES_TEXTS.AVAILABLE_COLUMNS_HINT}
            </Text>
            <div className="flex flex-wrap gap-4">
              {availableColumns.map((column) => (
                <Chip
                  key={column}
                  clickable
                  onClick={() => handleCopyToken(column)}
                  label={
                    <span className="flex items-center gap-4">
                      {column}
                      <CopyToClipboardIcon />
                    </span>
                  }
                  size="small"
                  className="bg-slate-100"
                />
              ))}
            </div>
          </div>
        )}

        {isValidating && (
          <div className="col-span-2">
            <div className="flex items-center gap-8">
              <LoadingIndicator size="small" />
              <Text variant={Text.VARIANTS.XS}>{REPORT_TEMPLATES_TEXTS.VALIDATING_VARIABLES}</Text>
            </div>
          </div>
        )}

        {hasUnmatchedVariables && !isValidating && (
          <div className="col-span-2">
            <AlertBanner
              note={getUnmatchedVariablesWarning(
                validationState?.unmatchedVariables.length ?? 0,
                validationState?.unmatchedVariables ?? []
              )}
              alertType={AlertBanner.ALERT_TYPES.WARNING}
            />
          </div>
        )}

        {hasMatchedVariables && !hasUnmatchedVariables && !isValidating && (
          <div className="col-span-2">
            <AlertBanner
              note={getAllVariablesMatchedText(validationState?.matchedVariables.length ?? 0)}
              alertType={AlertBanner.ALERT_TYPES.DONE}
            />
          </div>
        )}

        {!hasVariables && (
          <div className="col-span-2">
            <Text variant={Text.VARIANTS.XS} className="text-slate-500">
              {REPORT_TEMPLATES_TEXTS.NO_TEMPLATE_VARIABLES}
            </Text>
          </div>
        )}
      </div>

      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </div>
  );
};

export default SheetConfigurationCard;
